import type { ContentObjectView, Poll, PollContentObjectDetail, PollContentObjectList } from 'models';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canDeleteContentObjectProfile, canEditContentObjectProfile, usePermissions } from 'permissions';
import { BookmarkLineIcon, Delete2LineIcon, Flag1LineIcon, type MenuProps, PencilLineIcon, message } from 'ui';
import { useUser } from 'features/auth';
import { useUnbookmarkObjectMutation } from 'features/bookmark';
import { EditPoll } from 'features/polls';
import { useReportContentObjectModalConfirm } from 'features/report';
import { useReloadContentObjectMutation } from '../../../queries';
import { useDeletePollConfirmModal } from './useDeletePollConfirmModal';

type P = {
  item: PollContentObjectDetail | PollContentObjectList;
  view: ContentObjectView;
};

type UseCreateActionsReturn = {
  actions: MenuProps['items'];
  contexts?: ReactNode[];
  editModal?: ReactNode;
};

const toPoll = (item: Extract<PollContentObjectList, { contentableType: 'Community' }>): Poll => ({
  attachments: item.attachments,
  description: item.poll.description,
  endTime: item.poll.endTime,
  htmlDescription: item.poll.htmlDescription,
  id: item.poll.id,
  pinned: item.pinned,
  postInCommunities: [{ id: item.postInCommunities[0].id, name: item.postInCommunities[0].name }],
  questions: item.poll.questions,
  startTime: item.poll.startTime,
  title: item.poll.title,
});

/**
 * @deprecated This is only for port to tsx, dont inspire
 */
export const useCreateActions = ({ item, view }: P): UseCreateActionsReturn => {
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);
  const { t } = useTranslation();
  const { confirm: confirmDeletePoll, contextHolder: deleteContentObjectContextHolder } = useDeletePollConfirmModal();

  const { confirm: confirmReportPoll, contextHolder: reportContentObjectContextHolder } =
    useReportContentObjectModalConfirm();

  const { canReportContentObject } = usePermissions();
  const { railsUserData: viewer } = useUser();
  const { mutate: unbookmark } = useUnbookmarkObjectMutation();
  const { mutateAsync: reloadContentObject } = useReloadContentObjectMutation();

  return useMemo(() => {
    if (view === 'bookmark') {
      return {
        actions: [
          {
            icon: <BookmarkLineIcon size="small" />,
            key: 'unbookmark',
            label: t('Unbookmark'),
            onClick() {
              unbookmark(
                { contentObject: item, type: 'rails' },
                {
                  onError() {
                    message.error('Something went wrong.');
                  },
                  onSuccess() {
                    message.success('Successfully unbookmarked');
                  },
                },
              );
            },
          },
        ],
      };
    }

    const actions = [];

    if (canEditContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        icon: <PencilLineIcon size="small" />,
        key: 'update',
        label: t('Edit'),
        onClick() {
          setIsEditModalOpened(true);
        },
      });
    }

    if (canReportContentObject({ contentObject: item })) {
      actions.push({
        icon: <Flag1LineIcon size="small" />,
        key: 'report',
        label: t('Report'),
        onClick() {
          confirmReportPoll({
            id: item.id,
            onSuccess() {
              message.success('Successfully reported');
            },
            title: 'Are you sure you want to report this poll?',
          });
        },
      });
    }

    if (canDeleteContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        danger: true,
        icon: <Delete2LineIcon color="error" size="small" />,
        key: 'delete',
        label: t('Delete'),
        onClick() {
          confirmDeletePoll({
            item,
            onSuccess() {
              message.success('Successfully deleted');
            },
            title: 'Are you sure you want to delete this poll?',
          });
        },
      });
    }

    const editModal =
      item.contentableType === 'Community' ? (
        <EditPoll
          isOpened={isEditModalOpened}
          onCancel={() => {
            setIsEditModalOpened(false);
          }}
          onSuccess={async () => {
            await reloadContentObject({ filter: { id: item.id } });
            setIsEditModalOpened(false);
          }}
          poll={toPoll(item)}
        />
      ) : undefined;

    return {
      actions,
      contexts: [deleteContentObjectContextHolder, reportContentObjectContextHolder],
      editModal,
    };
  }, [
    view,
    canReportContentObject,
    item,
    viewer,
    isEditModalOpened,
    reloadContentObject,
    deleteContentObjectContextHolder,
    reportContentObjectContextHolder,
    t,
    confirmReportPoll,
    confirmDeletePoll,
    unbookmark,
  ]);
};
